import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useParams, useRevalidator } from 'react-router-dom';
import { client } from '~/client';
import { JobTemplateFilters } from '~/components/job-template/JobTemplateFilters';
import { JobTemplateList } from '~/components/job-template/JobTemplateList';
import {
  getTemplateBehavior,
  type TemplateBehavior,
} from '~/components/job/AssetDrivenTemplateIndicator';
import { Split } from '~/components/Split';
import { graphql } from '~/gql';
import { getFragmentData } from '~/gql';
import { JobTemplate, Status } from '~/gql/graphql';
import { JobTemplateFields } from '~/graphql/fragment/JobTemplateFields';
import { unique } from '~/helpers/array';
import parseFilters from '~/helpers/parseFilters';
import { isSystemGeneratedTemplate } from '~/helpers/templateHelpers';
import { useLocalStorage } from '~/hooks';
import { useSearch } from '~/hooks/useSearch';
import { JobsNav } from '~/layouts/nav/JobsNav';
import { Option } from '~/types';

// Navigation component that shows JobsNav consistently
const TemplatesNav = () => (
  <>
    <JobsNav />
  </>
);

const SEARCH_OPTIONS = {
  keys: [
    'name',
    'status',
    {
      name: 'includeAttributes',
      getFn(jobTemplate: any) {
        return jobTemplate.includeAttributes?.join(', ') ?? '';
      },
    },
  ],
};

const ListJobTemplatesDocument = graphql(`
  query ListJobTemplates($status: Status) {
    jobTemplates(status: $status) {
      id
      name
      ...JobTemplateFields
    }
  }
`);

export async function loader() {
  const { data, error } = await client.query(
    ListJobTemplatesDocument,
    { status: undefined },
    { requestPolicy: 'cache-and-network' }
  );

  if (error || !data) {
    throw new Response(error?.message, { status: 500 });
  }

  return data;
}

type TContext = {
  templates?: Awaited<ReturnType<typeof loader>>['jobTemplates'];
  reload: () => void;
};

type TOption = {
  value: string;
  label: string;
};

export const JobTemplateListContext = createContext<TContext>({} as TContext);
export const useJobTemplateListContext = () => {
  const context = useContext(JobTemplateListContext);

  if (!context) {
    throw new Error('Must be used within JobTemplateListContext');
  }

  return context;
};

const parseAttributeOptions = (templates: JobTemplate[]) => {
  const listOfOpts: string[][] = [];

  templates?.forEach((template) => {
    if (template?.includeAttributes) {
      listOfOpts.push(template.includeAttributes);
    }
  });

  return unique(listOfOpts.flat(1)).map((opt): Option => {
    const optParts = opt.split('\\');
    const group = optParts.length > 1 ? optParts[0] : undefined;
    const optionLabel =
      optParts.length > 1 ? optParts.splice(1).join('\\') : opt;
    return {
      $group: group?.trimEnd(),
      value: opt,
      label: optionLabel.trimStart(),
    };
  });
};

export default function TemplatesRoute() {
  const localStorage = useLocalStorage();
  const data = useLoaderData() as Awaited<ReturnType<typeof loader>>;
  const revalidator = useRevalidator();
  const reload = () => revalidator.revalidate();
  const templates = data.jobTemplates;
  const { templateId } = useParams();
  const [query, setQuery] = useState('');
  const { results, search } = useSearch(templates, SEARCH_OPTIONS);
  const attributesFilterOptions: TOption[] = parseAttributeOptions(
    templates as JobTemplate[]
  );
  const { t } = useTranslation(['translation']);
  const [filtersValue, setFiltersValue] = useState<URLSearchParams>(
    new URLSearchParams([['status', 'Active']])
  );
  const hasInitializedSearch = useRef(false);

  // Set localStorage to remember current page
  useEffect(() => {
    localStorage.set('nav.more', '/job-templates');
  }, []);

  // Apply initial filters on mount
  // Note: filtersValue and query are accessed from closure but not in dependencies
  // since hasInitializedSearch ref gate ensures this only runs once when templates load
  useEffect(() => {
    if (templates && !hasInitializedSearch.current) {
      hasInitializedSearch.current = true;
      const parsedFilters = parseFilters(filtersValue);
      // Exclude systemGenerated from search filters as it's handled separately in filteredResults
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { systemGenerated: _systemGenerated, ...searchFilters } =
        parsedFilters;
      search(query, searchFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, search]);

  // Memoize the string representation of filtersValue for stable comparison
  const filtersValueString = useMemo(
    () => filtersValue.toString(),
    [filtersValue]
  );

  // Apply system-generated and template type filters after search results
  const filteredResults = useMemo(() => {
    if (!results) return results;

    // Reconstruct URLSearchParams from string for stable comparison
    const filters = new URLSearchParams(filtersValueString);
    const parsedFilters = parseFilters(filters);
    const systemGeneratedFilter = parsedFilters.systemGenerated?.[0];
    const templateTypeFilter = parsedFilters.templateType?.[0];

    let filtered = results;

    // Apply template type filter
    if (templateTypeFilter === 'job') {
      filtered = filtered.filter((template) => {
        const templateData = getFragmentData(JobTemplateFields, template);
        return getTemplateBehavior(templateData) !== 'asset_rules';
      });
    } else if (templateTypeFilter === 'asset') {
      filtered = filtered.filter((template) => {
        const templateData = getFragmentData(JobTemplateFields, template);
        return getTemplateBehavior(templateData) === 'asset_rules';
      });
    }
    // 'all' or no value means show all types

    // Apply system-generated filter
    // Default: exclude system-generated templates (show only user-created)
    // Toggle ON (value === 'system'): show only system-generated templates
    // Toggle OFF (no value or empty): show only user-created templates
    if (systemGeneratedFilter === 'system') {
      filtered = filtered.filter((template) =>
        isSystemGeneratedTemplate(template)
      );
    } else {
      // Toggle is OFF - show only user-created templates
      filtered = filtered.filter(
        (template) => !isSystemGeneratedTemplate(template)
      );
    }

    return filtered;
  }, [results, filtersValueString]);

  return (
    <JobTemplateListContext.Provider value={{ templates, reload }}>
      {/* Authorization is intentionally disabled for this view while pricing is being implemented. */}
      {/*  <AuthGate action='manage' subject='feat.templates'>  */}
      <Split
        nav={<TemplatesNav />}
        filters={
          <JobTemplateFilters
            filters={[
              {
                name: 'status',
                label: t('status'),
                options: [
                  { label: 'Active', value: Status.Active },
                  { label: 'Inactive', value: Status.Inactive },
                ],
                searchable: false,
                type: 'select',
              },
              {
                name: 'templateType',
                label: t('template_type'),
                options: [
                  { label: t('job_based', 'Job based'), value: 'job' },
                  {
                    label: t('job:assetDrivenTemplate.badge', 'Asset-driven'),
                    value: 'asset',
                  },
                ],
                searchable: false,
                type: 'select',
              },
              {
                name: 'systemGenerated',
                label: t('system_generated'),
                type: 'toggle',
                toggle: 'system',
              },
              {
                name: 'includeAttributes',
                label: t('attribute'),
                options: attributesFilterOptions,
                type: 'select',
              },
            ]}
            filtersValue={filtersValue}
            setFiltersValue={setFiltersValue}
            query={query}
            onSubmit={({ query, filters }) => {
              setFiltersValue(filters);
              setQuery(query);
              const parsedFilters = parseFilters(filters);
              // Exclude systemGenerated and templateType from search filters as they're handled separately in filteredResults
              const { systemGenerated, templateType, ...searchFilters } =
                parsedFilters;
              search(query, searchFilters);
            }}
          />
        }
        main={
          <JobTemplateList
            templates={filteredResults ?? []}
            selectedId={templateId}
          />
        }
        mobileAction='Template'
      />
      {/*  </AuthGate> */}
    </JobTemplateListContext.Provider>
  );
}
